import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { fetchTsrFoodCoupons, saveTsrFoodCoupon } from '../../api/tsrFoodApi';
import TsrAdminDataTable from '../../components/TsrAdminDataTable';
import { formatAmount, toBooleanFromBit } from '../../utils/orderFoodUtils';

const emptyForm = {
  TsrFoodCouponId: null,
  CouponCode: '',
  DiscountType: 'PERCENT',
  DiscountValue: '',
  MinOrderAmount: '0',
  MaxDiscountAmount: '',
  ValidFrom: '',
  ValidTo: '',
  UsageLimit: '',
  UsagePerCustomer: '',
  IsActive: true
};

const DISCOUNT_TYPES = [
  { value: 'PERCENT', label: 'Percent (%)' },
  { value: 'FIXED', label: 'Fixed amount' },
  { value: 'FIXED_PER_PERSON', label: 'Fixed per person' }
];

function formatDateInput(value) {
  if (!value) {
    return '';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  return date.toISOString().slice(0, 10);
}

function formatDiscountLabel(coupon) {
  if (coupon.DiscountType === 'PERCENT') {
    return `${coupon.DiscountValue}%`;
  }
  if (coupon.DiscountType === 'FIXED_PER_PERSON') {
    return `${formatAmount(coupon.DiscountValue)}/person`;
  }
  return formatAmount(coupon.DiscountValue);
}

function TsrAdminCouponsPage() {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [searchText, setSearchText] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const loadCoupons = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await fetchTsrFoodCoupons();
      setCoupons(Array.isArray(response?.data) ? response.data : []);
    } catch (err) {
      setError(err.message || 'Unable to load coupons.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCoupons();
  }, []);

  const filteredCoupons = useMemo(() => {
    const normalizedSearch = searchText.trim().toLowerCase();
    if (!normalizedSearch) {
      return coupons;
    }
    return coupons.filter((coupon) => (
      String(coupon.CouponCode || '').toLowerCase().includes(normalizedSearch)
      || String(coupon.DiscountType || '').toLowerCase().includes(normalizedSearch)
    ));
  }, [coupons, searchText]);

  const openCreateForm = () => {
    setForm({ ...emptyForm });
    setShowForm(true);
    setError('');
  };

  const openEditForm = useCallback((coupon) => {
    setForm({
      TsrFoodCouponId: coupon.TsrFoodCouponId,
      CouponCode: coupon.CouponCode || '',
      DiscountType: coupon.DiscountType || 'PERCENT',
      DiscountValue: String(coupon.DiscountValue ?? ''),
      MinOrderAmount: String(coupon.MinOrderAmount ?? '0'),
      MaxDiscountAmount: coupon.MaxDiscountAmount == null ? '' : String(coupon.MaxDiscountAmount),
      ValidFrom: formatDateInput(coupon.ValidFrom),
      ValidTo: formatDateInput(coupon.ValidTo),
      UsageLimit: coupon.UsageLimit == null ? '' : String(coupon.UsageLimit),
      UsagePerCustomer: coupon.UsagePerCustomer == null ? '' : String(coupon.UsagePerCustomer),
      IsActive: toBooleanFromBit(coupon.IsActive, true)
    });
    setShowForm(true);
    setError('');
  }, []);

  const columns = useMemo(() => [
    {
      name: 'Code',
      sortable: true,
      selector: (row) => row.CouponCode || '',
      cell: (row) => <span className="fw-semibold">{row.CouponCode}</span>
    },
    {
      name: 'Type',
      sortable: true,
      selector: (row) => row.DiscountType || ''
    },
    {
      name: 'Value',
      sortable: true,
      selector: (row) => Number(row.DiscountValue) || 0,
      cell: (row) => formatDiscountLabel(row)
    },
    {
      name: 'Min order',
      sortable: true,
      selector: (row) => Number(row.MinOrderAmount) || 0,
      cell: (row) => formatAmount(row.MinOrderAmount || 0)
    },
    {
      name: 'Valid',
      sortable: true,
      selector: (row) => row.ValidFrom || '',
      cell: (row) => (
        <small>
          {row.ValidFrom ? formatDateInput(row.ValidFrom) : '—'}
          {' '}
          to
          {' '}
          {row.ValidTo ? formatDateInput(row.ValidTo) : '—'}
        </small>
      )
    },
    {
      name: 'Usage',
      sortable: true,
      selector: (row) => Number(row.UsageCount) || 0,
      cell: (row) => (
        <small>
          {row.UsageCount || 0}
          {row.UsageLimit != null ? ` / ${row.UsageLimit}` : ''}
        </small>
      )
    },
    {
      name: 'Status',
      sortable: true,
      selector: (row) => (toBooleanFromBit(row.IsActive, false) ? 1 : 0),
      cell: (row) => (
        <span className={`badge ${toBooleanFromBit(row.IsActive, false) ? 'bg-success' : 'bg-secondary'}`}>
          {toBooleanFromBit(row.IsActive, false) ? 'Active' : 'Inactive'}
        </span>
      )
    },
    {
      name: 'Actions',
      cell: (row) => (
        <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => openEditForm(row)}>
          Edit
        </button>
      ),
      ignoreRowClick: true,
      width: '100px'
    }
  ], [openEditForm]);

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const validateForm = () => {
    if (!form.CouponCode.trim()) return 'Coupon code is required.';
    if (!form.DiscountValue || Number(form.DiscountValue) <= 0) return 'Discount value must be greater than zero.';
    if (form.ValidFrom && form.ValidTo && form.ValidTo < form.ValidFrom) {
      return 'Valid To must be on or after Valid From.';
    }
    return '';
  };

  const handleSave = async () => {
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setSaving(true);
      setError('');
      await saveTsrFoodCoupon({
        ...form,
        CouponCode: form.CouponCode.trim().toUpperCase(),
        DiscountValue: Number(form.DiscountValue),
        MinOrderAmount: Number(form.MinOrderAmount) || 0,
        MaxDiscountAmount: form.MaxDiscountAmount === '' ? null : Number(form.MaxDiscountAmount),
        UsageLimit: form.UsageLimit === '' ? null : Number(form.UsageLimit),
        UsagePerCustomer: form.UsagePerCustomer === '' ? null : Number(form.UsagePerCustomer),
        ValidFrom: form.ValidFrom || null,
        ValidTo: form.ValidTo || null,
        IsActive: form.IsActive ? 1 : 0
      });
      setShowForm(false);
      setForm({ ...emptyForm });
      await loadCoupons();
    } catch (err) {
      setError(err.message || 'Unable to save coupon.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h4 className="mb-1">Coupon Configuration</h4>
          <p className="text-muted mb-0">Create and manage discount codes for TSR food orders.</p>
        </div>
        <button type="button" className="btn btn-primary" onClick={openCreateForm}>
          Add coupon
        </button>
      </div>

      {error ? <div className="alert alert-danger py-2">{error}</div> : null}

      <div className="row g-3 mb-3">
        <div className="col-md-4">
          <input
            type="text"
            className="form-control"
            placeholder="Search coupons..."
            value={searchText}
            onChange={(event) => setSearchText(event.target.value)}
          />
        </div>
      </div>

      <TsrAdminDataTable
        columns={columns}
        data={filteredCoupons}
        loading={loading}
        defaultSortFieldId={1}
        noDataComponent="No coupons configured yet."
      />

      {showForm ? (
        <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.45)' }}>
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{form.TsrFoodCouponId ? 'Edit coupon' : 'Create coupon'}</h5>
                <button type="button" className="btn-close" onClick={() => setShowForm(false)} />
              </div>
              <div className="modal-body">
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label">Coupon code</label>
                    <input
                      type="text"
                      className="form-control"
                      value={form.CouponCode}
                      onChange={(event) => updateField('CouponCode', event.target.value.toUpperCase())}
                      placeholder="THALI10"
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Discount type</label>
                    <select
                      className="form-select"
                      value={form.DiscountType}
                      onChange={(event) => updateField('DiscountType', event.target.value)}
                    >
                      {DISCOUNT_TYPES.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Discount value</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      className="form-control"
                      value={form.DiscountValue}
                      onChange={(event) => updateField('DiscountValue', event.target.value)}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Minimum order amount</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      className="form-control"
                      value={form.MinOrderAmount}
                      onChange={(event) => updateField('MinOrderAmount', event.target.value)}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Max discount amount</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      className="form-control"
                      value={form.MaxDiscountAmount}
                      onChange={(event) => updateField('MaxDiscountAmount', event.target.value)}
                      placeholder="For percent coupons"
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Valid from</label>
                    <input
                      type="date"
                      className="form-control"
                      value={form.ValidFrom}
                      onChange={(event) => updateField('ValidFrom', event.target.value)}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Valid to</label>
                    <input
                      type="date"
                      className="form-control"
                      value={form.ValidTo}
                      onChange={(event) => updateField('ValidTo', event.target.value)}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Total usage limit</label>
                    <input
                      type="number"
                      min="1"
                      className="form-control"
                      value={form.UsageLimit}
                      onChange={(event) => updateField('UsageLimit', event.target.value)}
                      placeholder="Leave blank for unlimited"
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Usage per customer</label>
                    <input
                      type="number"
                      min="1"
                      className="form-control"
                      value={form.UsagePerCustomer}
                      onChange={(event) => updateField('UsagePerCustomer', event.target.value)}
                      placeholder="Leave blank for unlimited"
                    />
                  </div>
                  <div className="col-12">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={form.IsActive}
                        onChange={(event) => updateField('IsActive', event.target.checked)}
                        id="coupon-active"
                      />
                      <label className="form-check-label" htmlFor="coupon-active">Active</label>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-light" onClick={() => setShowForm(false)}>Cancel</button>
                <button type="button" className="btn btn-primary" onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving...' : 'Save coupon'}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default TsrAdminCouponsPage;
